import { SetStateAction, useState } from "react";
import isEqual from "lodash-es/isEqual";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { AirFilterType, SeaFilterType } from "features/shared/filter.types";
import { SeaOrderQuery } from "entities/SeaSavedCard";
import { SeaWhereQuery } from "entities/SeaSavedCard";
import { usePrevious } from "shared/hooks/usePrevious.hook";

type UseSeaFilterContextReturnType = {
  seaFilter: SeaFilterType;
  seaFilterInnerValues: SeaFilterType;
  setSeaInnerWhereQuery: (valueAction: SetStateAction<SeaWhereQuery>) => void;
  setSeaInnerOrderQuery: (valueAction: SetStateAction<SeaOrderQuery[]>) => void;
  resetSeaInnerFilter: () => void;
  saveSeaFilter: (filterValue: SeaFilterType) => void;
  resetSeaFilter: () => void;
  seaFilterChanged: () => boolean;
  isSeaFilter: (filter: SeaFilterType | AirFilterType) => filter is SeaFilterType;
};

export const useSeaFilterContext = (): UseSeaFilterContextReturnType => {
  const [seaFilter, setSeaFilter] = useState<SeaFilterType>({
    where: {},
    order: [],
    filterType: ShipmentType.SEA,
  });
  const [seaFilterInnerValues, setSeaFilterInnerValues] = useState<SeaFilterType>(seaFilter);

  const previousSeaFilter = usePrevious<SeaFilterType>(seaFilter);

  const setSeaInnerWhereQuery = (valueAction: SetStateAction<SeaWhereQuery>) => {
    setSeaFilterInnerValues((prev: SeaFilterType) => {
      return {
        ...prev,
        where: typeof valueAction === "function" ? valueAction(prev.where) : valueAction,
      };
    });
  };

  const setSeaInnerOrderQuery = (valueAction: SetStateAction<SeaOrderQuery[]>) => {
    setSeaFilterInnerValues((prev: SeaFilterType) => {
      return {
        ...prev,
        order: typeof valueAction === "function" ? valueAction(prev.order) : valueAction,
      };
    });
  };

  const resetSeaInnerFilter = () => {
    setSeaFilterInnerValues({ where: {}, order: [], filterType: ShipmentType.SEA });
  };

  const saveSeaFilter = (seaFilterValue: SeaFilterType) => {
    setSeaFilter(seaFilterValue);
  };

  const resetSeaFilter = () => {
    setSeaFilter({ where: {}, order: [], filterType: ShipmentType.SEA });
    resetSeaInnerFilter();
  };

  const seaFilterChanged = (): boolean => {
    if (!previousSeaFilter) return false;
    const isPreviousSeaFilterEmpty =
      Object.keys(previousSeaFilter.where).length === 0 && previousSeaFilter.order.length === 0;
    if (isPreviousSeaFilterEmpty) return false;

    const whereChanged = !isEqual(previousSeaFilter.where, seaFilter.where);
    const orderChanged = !isEqual(previousSeaFilter.order, seaFilter.order);

    return whereChanged || orderChanged;
  };

  return {
    seaFilter,
    seaFilterInnerValues,
    setSeaInnerWhereQuery,
    setSeaInnerOrderQuery,
    resetSeaInnerFilter,
    saveSeaFilter,
    resetSeaFilter,
    seaFilterChanged,
    isSeaFilter: (filter): filter is SeaFilterType => filter.filterType === ShipmentType.SEA,
  };
};
